import React from 'react';
import styled, { css } from 'styled-components';
import { Classable, HasChildren, Shapeable } from '@shapeable/types';
import { breakpoints, theme, ColorTokens } from '@shapeable/theme';
import { classNames } from '@shapeable/utils';
import { EntityImage, InfoTooltip, useLang } from '@shapeable/ui';
import { JAPAN_MAP_THUMBNAIL } from '../../data';
import { ChevronRightIconGlyph, InfoIcon } from '@shapeable/icons';

const cls = classNames('aside-map-button');

// -------- Types -------->

// -------- Props -------->

export type AsideMapButtonProps = Classable & HasChildren & {
  colors?: ColorTokens.TextBackground;
  onClick?: () => void;
  infoDescription?: string;
}

export const AsideMapButtonDefaultProps: AsideMapButtonProps = {
  infoDescription: 'This link will take you to an interactive map where you can explore the location in more detail.',
  colors: {
    background: 'canvas',
    text: 'mid',
  }
};

// -------- Child Component Props -------->

type ContainerProps = {
  colors?: ColorTokens.TextBackground;
}

// -------- Styles -------->

const ContainerStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: row;
    align-items: center;
    border-radius: 16px 16px 16px 16px;
    background-color: ${theme.COLOR('canvas')};
    transition: ${theme.TRANSITION('box-shadow')};
    box-shadow: 2px 4px 6px rgba(0, 0, 0, 0.1);

    &:hover {
      cursor: pointer;
      box-shadow: 0px 5px 10px rgba(0, 0, 0, 0.2);
    }
  `,
});

const ImageStyles = breakpoints({
  base: css`
    width: 80px;
    height: 80px;

    img {
      border-radius: 16px 0 0 16px;
    }
  `,
});

const BodyStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: ${theme.UNIT(3)} ${theme.UNIT(3)};
    flex-grow: 1;
    font-weight: 500;
    color: ${theme.COLOR('primary')};

  `,
});

const IconsStyles = breakpoints({
  base: css`
    display: inline-flex;
    align-items: center;
    gap: ${theme.UNIT(4)};
    margin-right: ${theme.UNIT(10)};
  `,
});

const TooltipStyles = breakpoints({
  base: css`
    display: flex;
    align-items: center;
  `,
});

const TooltipModalStyles = breakpoints({
  base: css`
    
  `,
});

// -------- Components -------->

const My = {
  Container: styled.div<ContainerProps>`${ContainerStyles}`,
    Image: styled(EntityImage)`${ImageStyles}`,
    Body: styled.div`${BodyStyles}`,
      Icons: styled.div`${IconsStyles}`,
    
    Tooltip: styled(InfoTooltip)`${TooltipStyles}`,
      TooltipModal: styled.div`${TooltipModalStyles}`,
    
};

export const AsideMapButton: Shapeable.FC<AsideMapButtonProps> = (props) => {
  const { className, children, colors, onClick, infoDescription } = props;
  
  const t = useLang();

  return (
    <My.Container onClick={onClick} className={cls.name(className)} colors={colors}>
      <My.Image entity={JAPAN_MAP_THUMBNAIL} />
      <My.Body>
        {children}
        <My.Icons>
          <My.Tooltip
            id="aside-map-button-tooltip"
            showIcon={true}
            iconSize={18}
            colors={{ fill: 'mid' }}
            tooltip={(
              <My.TooltipModal>
                {t(infoDescription)}
              </My.TooltipModal>
            )}>
          </My.Tooltip>
          <ChevronRightIconGlyph size={18} colors={{ fill: 'mid' }} />
        </My.Icons>
      </My.Body>
    </My.Container>
  )
};

AsideMapButton.cls = cls;